import { Activity, memo, useEffect, useMemo, useState } from 'react';
// import { memo, useLayoutEffect, useState } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

// project imports
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import { MenuOrientation } from 'config';
import weightrax from 'menu-items/weightrax';
import useConfig from 'hooks/useConfig';

// import { Menu } from 'menu-items/widget';
import { HORIZONTAL_MAX_ITEMS } from 'config';
import { useGetMenuMaster } from 'api/menu';
import { useGetActiveModulePageNavItems } from 'api/modulePage';
import usePermissions from 'hooks/usePermissions';
import useTenantSelection from 'hooks/useTenantSelection';
import { APP_MANAGEMENT_ROUTE, hasAnyAppManagementAccess } from 'utils/appManagementAccess';
import { findAdminTabByUrl } from 'utils/adminAccess';
import { MAINTENANCE_ROUTE, hasAnyMaintenanceAccess } from 'utils/maintenanceAccess';
import { ADD_VISIT_ROUTE, ONSITE_VEHICLES_ROUTE } from 'utils/visitAccess';

// types
import { NavItemType } from 'types';

// Diagnostic tracing for the tenant-switch path. The menu composes two independent async sources —
// the tenant's module pages and the current user's permissions — and hides whole sections whenever
// either is absent, so a wrong menu is almost always a timing question: which source was late, and
// what did the permission set look like at the moment the filter ran.
//
// Dev builds only; set localStorage.debugMenu = '0' to silence.
const DEBUG_MENU = import.meta.env.DEV && localStorage.getItem('debugMenu') !== '0';

function logMenu(event: string, detail: Record<string, unknown>) {
  if (!DEBUG_MENU) return;

  console.log(`%c[menu] ${event}`, 'color:#2196f3;font-weight:600', detail);
}

// Flattens the tree to "id (type)" strings so a whole menu shape fits on one log line.
function describeItems(items: NavItemType[]): string[] {
  return items.flatMap((item) => {
    const label = `${item.id ?? item.url ?? '?'}${item.type ? `(${item.type})` : ''}`;
    return item.children?.length ? [label, ...describeItems(item.children).map((child) => `  ${child}`)] : [label];
  });
}

// Recursively removes any nav item (and its descendants) matching the predicate.
function filterNavItems(items: NavItemType[], isHidden: (item: NavItemType) => boolean): NavItemType[] {
  return items
    .filter((item) => !isHidden(item))
    .map((item) => (item.children ? { ...item, children: filterNavItems(item.children, isHidden) } : item));
}

// Removes any 'collapse' nav item that ends up with no children left after filtering, so an
// entirely-inaccessible dropdown (eg. "Admin" with every child hidden) doesn't show up empty.
function pruneEmptyCollapses(items: NavItemType[]): NavItemType[] {
  return items
    .map((item) => (item.children ? { ...item, children: pruneEmptyCollapses(item.children) } : item))
    .filter((item) => item.type !== 'collapse' || (item.children && item.children.length > 0));
}

// The horizontal bar shows one entry per top-level group, and the menu is a single "Main Menu"
// group — so lift that group's children up a level. A plain link becomes a group with a url (a
// bar button); a collapse becomes a group with children (a bar dropdown).
function promoteGroupChildren(items: NavItemType[]): NavItemType[] {
  return items.flatMap((group) =>
    group.type === 'group' && !group.url && group.children?.length
      ? group.children.map((child) => ({ ...child, type: 'group' }) as NavItemType)
      : [group]
  );
}

// ==============================|| SIDEBAR MENU LIST ||============================== //

function MenuList() {
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const upLG = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const upXL = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

  const {
    state: { menuOrientation }
  } = useConfig();
  // const { menuLoading } = useGetMenu();
  const { menuMaster } = useGetMenuMaster();
  const { modulePageNavItems, modulePageNavItemError, modulePageNavItemLoading, modulePageNavItemPending } =
    useGetActiveModulePageNavItems();
  const { hasPermission, permissionCodes, loading: permissionsLoading, error: permissionsError } = usePermissions();
  const { tenantId, selectedCompanySiteResult } = useTenantSelection();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  // Each source logged as it settles, so the ordering across a tenant switch is visible.
  useEffect(() => {
    logMenu('tenant changed', { tenantId });
  }, [tenantId]);

  useEffect(() => {
    logMenu('permissions', {
      tenantId,
      loading: permissionsLoading,
      codeCount: permissionCodes.length,
      hasManageApplication: permissionCodes.includes('manage:application')
    });
  }, [tenantId, permissionsLoading, permissionCodes]);

  useEffect(() => {
    logMenu('module pages', {
      tenantId,
      loading: modulePageNavItemLoading,
      error: modulePageNavItemError ? String(modulePageNavItemError) : null,
      topLevel: modulePageNavItems ? describeItems([modulePageNavItems]) : null
    });
  }, [tenantId, modulePageNavItemLoading, modulePageNavItemError, modulePageNavItems]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;
  const resolvedMenuItems = useMemo(() => {
    let items: NavItemType[];
    let source: string;
    if (modulePageNavItems) {
      items = [modulePageNavItems];
      source = 'module-pages';
    } else if (modulePageNavItemError) {
      items = [weightrax];
      source = 'static-fallback(module-page error)';
    } else {
      items = [];
      source = 'none(module-pages not loaded)';
    }

    // Fail closed: hide App Management, Admin, and Maintenance items while permissions are
    // still loading, and unless the user holds at least one of the relevant section permissions.
    const checkPermission = (code: string) => !permissionsLoading && hasPermission(code);

    const hidden: string[] = [];
    items = filterNavItems(items, (item) => {
      const id = item.id ?? item.url ?? '?';

      if (item.id === 'application-management' || item.url === APP_MANAGEMENT_ROUTE) {
        const isHidden = !hasAnyAppManagementAccess(checkPermission);
        if (isHidden) hidden.push(`${id} <- no app-management permission`);
        return isHidden;
      }

      if (item.id === 'maintenance' || item.url === MAINTENANCE_ROUTE) {
        const isHidden = !hasAnyMaintenanceAccess(checkPermission);
        if (isHidden) hidden.push(`${id} <- no maintenance permission`);
        return isHidden;
      }

      // Raising a visit needs a site to raise it at, and the onsite list belongs to exactly one
      // site; both come from the header selection. Hidden rather than disabled: a link that leads
      // only to "pick a site first" is worse than no link, and the header is where that choice is
      // made anyway.
      if (item.url === ADD_VISIT_ROUTE || item.url === ONSITE_VEHICLES_ROUTE) {
        const isHidden = selectedCompanySiteResult?.siteId == null;
        if (isHidden) hidden.push(`${id} <- no site selected`);
        return isHidden;
      }

      const adminTab = findAdminTabByUrl(item.url);
      if (adminTab) {
        const isHidden = !adminTab.permissions.some((code) => checkPermission(code));
        if (isHidden) hidden.push(`${id} <- needs one of [${adminTab.permissions.join(', ')}]`);
        return isHidden;
      }

      return false;
    });

    const beforePrune = describeItems(items);
    items = pruneEmptyCollapses(items);

    logMenu('rebuilt', {
      tenantId,
      source,
      permissions: permissionsLoading ? 'LOADING (everything permission-gated is hidden)' : `${permissionCodes.length} codes`,
      permissionCodes: permissionsLoading ? undefined : permissionCodes,
      hiddenByPermission: hidden.length ? hidden : 'none',
      prunedEmptyCollapses: beforePrune.length - describeItems(items).length,
      finalMenu: describeItems(items)
    });

    return { items: isHorizontal ? promoteGroupChildren(items) : items };
  }, [
    isHorizontal,
    modulePageNavItemError,
    modulePageNavItems,
    hasPermission,
    permissionsLoading,
    permissionCodes,
    tenantId,
    selectedCompanySiteResult?.siteId
  ]);

  const [selectedID, setSelectedID] = useState<string | undefined>('');

  // Ten of the nav sections are permission-gated, so painting before permissions resolve shows a
  // deliberately-reduced menu that then expands. Wait until both sources have answered for the
  // selected tenant — but not past a permissions failure, or a lookup that never succeeds would
  // leave the menu waiting forever.
  const waitingForMenu = (permissionsLoading && !permissionsError) || modulePageNavItemPending;

  // The last menu built from settled sources. A tenant, company or site change reloads them, and
  // showing this until both settle again keeps the bar in place and then swaps it for the new
  // tenant's menu in one step, rather than dropping to a skeleton or passing through a menu built
  // from one tenant's pages and the other's permissions. The skeleton is only for the first load. The pages behind the links check permissions
  // themselves, so a link held over from the previous tenant cannot open anything it should not.
  const [heldMenuItems, setHeldMenuItems] = useState<typeof resolvedMenuItems | null>(null);
  if (!waitingForMenu && heldMenuItems !== resolvedMenuItems) setHeldMenuItems(resolvedMenuItems);
  const shownMenuItems = waitingForMenu ? heldMenuItems : resolvedMenuItems;
  const shownItems = shownMenuItems?.items ?? [];
  // const [menuItems, setMenuItems] = useState<{ items: NavItemType[] }>({ items: [] });

  // let widgetMenu = Menu();

  // useLayoutEffect(() => {
  //   const isFound = menuItem.items.some((element) => {
  //     if (element.id === 'group-widget') {
  //       return true;
  //     }
  //     return false;
  //   });
  //   if (menuLoading) {
  //     menuItem.items.splice(1, 0, widgetMenu);
  //     setMenuItems({ items: [...menuItem.items] });
  //   } else if (!menuLoading && widgetMenu?.id !== undefined && !isFound) {
  //     menuItem.items.splice(1, 1, widgetMenu);
  //     setMenuItems({ items: [...menuItem.items] });
  //   } else {
  //     setMenuItems({ items: [...menuItem.items] });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [menuLoading]);

  // last menu-item to show in horizontal menu bar
  const horizontalMaxItems = upXL ? HORIZONTAL_MAX_ITEMS.xl : upLG ? HORIZONTAL_MAX_ITEMS.lg : HORIZONTAL_MAX_ITEMS.md;
  const lastItem = isHorizontal ? horizontalMaxItems : null;

  let lastItemIndex = shownItems.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string;

  if (lastItem && lastItem < shownItems.length) {
    lastItemId = shownItems[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = shownItems.slice(lastItem - 1, shownItems.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const navItems = shownItems.slice(0, lastItemIndex + 1).map((item, index) => {
    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <List key={item.id}>
              <NavItem item={item} level={1} isParents setSelectedID={() => setSelectedID('')} />
              <Activity mode={!isHorizontal && index !== 0 ? 'visible' : 'hidden'}>
                <Divider sx={{ py: 0.5 }} />
              </Activity>
            </List>
          );
        }

        return (
          <NavGroup
            key={item.id}
            setSelectedID={setSelectedID}
            selectedID={selectedID}
            item={item}
            lastItem={lastItem!}
            remItems={remItems}
            lastItemId={lastItemId}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" align="center" sx={{ color: 'error.main' }}>
            Menu Items Error
          </Typography>
        );
    }
  });

  if (shownMenuItems == null) {
    // The bar is a single fixed-height row; a stack of skeleton lines does not fit it, so it stays
    // empty until the items arrive.
    if (isHorizontal) return null;
    return (
      <Box {...(drawerOpen && { sx: { mt: 1.5 } })} sx={{ px: 2 }}>
        <Skeleton variant="text" height={30} width="70%" />
        <Skeleton variant="rounded" height={32} sx={{ mt: 1 }} />
        <Skeleton variant="rounded" height={32} sx={{ mt: 1 }} />
        <Skeleton variant="rounded" height={32} sx={{ mt: 1 }} />
      </Box>
    );
  }

  return !isHorizontal ? <Box {...(drawerOpen && { sx: { mt: 1.5 } })}>{navItems}</Box> : <>{navItems}</>;
}

export default memo(MenuList);
